import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import CustomInput from '@/components/CustomInput/CustomInput';
import Button from '@/components/CustomButtons/Button';
import {
  Switch,
  FormControlLabel,
  TextField,
  FormControl,
  Typography,
  Box,
} from '@mui/material';
import { toast } from 'react-toastify';
import makeStyles from '@mui/styles/makeStyles';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import OrganizationFeatures from '../OrganizationFeatures';
import AudienceDesignator from '@/views/Managefile/data-mappings/AudienceDesignator';
import PaymentRailDesignator from '@/views/Managefile/data-mappings/PaymentRailDesignator';
import LogoUpload from '@/components/ui/LogoUpload';
import { PUBLIC_ASSETS_BASE_URL } from '@/config';
import { appendLogoToFormData } from '@/utils/logo-upload.utils';
import { isValidHttpUrl, normalizeClientWebsiteUrl } from '@/utils/url.util';
import styles from '@/assets/jss/material-dashboard-react/components/customInputStyle';
import { useConfirmation } from '@/context/ConfirmationContext';
import { useUpdateManagedAccountMembership } from '@/api/hooks/organizations/useUpdateManagedAccountMembership';
import { checkAdminPermission } from '@/views/Admin/CheckAdminPermissions';
import { useValidateCheckSequence } from '@/api/hooks/organizations/useValidateCheckSequence';
import CheckSequenceValidationBanner from './CheckSequenceValidationBanner';

const useStyles = makeStyles(styles);

const GeneralTab = ({
  organization,
  updateOrganization,
  onManagedAccountMembershipUpdated,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { requestConfirmation } = useConfirmation();
  const canManageManagedAccounts = checkAdminPermission({
    permissionKey: 'managed_accounts',
  });
  const {
    updateMembership,
    loading: membershipLoading,
  } = useUpdateManagedAccountMembership();
  const [isManaged, setIsManaged] = useState(
    organization.managedAccountId != null,
  );
  const [city, setCity] = useState(organization.city || '');
  const [state, setState] = useState(organization.state || '');
  const [county, setCounty] = useState(organization.county || '');
  const [sicCode, setSicCode] = useState(organization.sicCode || '');
  const [stateOfIncorporation, setStateOfIncorporation] = useState(
    organization.stateOfIncorporation || '',
  );
  const [incorporationDate, setIncorporationDate] = useState(() => {
    if (!organization.incorporationDate) return null;
    const parsedDate = moment(organization.incorporationDate);
    return parsedDate.isValid() ? parsedDate.toDate() : null;
  });
  const [taxIdExt, setTaxIdExt] = useState(organization.taxIdExt || '');
  const [taxId, setTaxId] = useState(organization.taxId || '');
  const [naicsCode, setNaicsCode] = useState(organization.naicsCode || '');
  const [helpEn, setHelpEn] = useState(organization.helpEn ?? '');
  const [helpFr, setHelpFr] = useState(organization.helpFr ?? '');
  const [helpEs, setHelpEs] = useState(organization.helpEs ?? '');
  const [ssoDirectLink, setSsoDirectLink] = useState(
    normalizeClientWebsiteUrl(organization.ssoDirectLink),
  );
  const [ssoDirectLinkError, setSsoDirectLinkError] = useState('');
  const [startingCheckNumber, setStartingCheckNumber] = useState(
    organization.startingCheckNumber
      ? String(organization.startingCheckNumber)
      : '',
  );
  const [checkSequenceEnabled, setCheckSequenceEnabled] = useState(
    organization.checkSequenceEnabled || false,
  );
  const [startingCheckNumberError, setStartingCheckNumberError] = useState('');
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [displayWidth, setDisplayWidth] = useState(null);
  const [displayHeight, setDisplayHeight] = useState(null);

  useEffect(() => {
    setIsManaged(organization.managedAccountId != null);
    setHelpEn(organization.helpEn ?? '');
    setHelpFr(organization.helpFr ?? '');
    setHelpEs(organization.helpEs ?? '');
    setSsoDirectLink(normalizeClientWebsiteUrl(organization.ssoDirectLink));
    setSsoDirectLinkError('');
    setStartingCheckNumber(
      organization.startingCheckNumber
        ? String(organization.startingCheckNumber)
        : '',
    );
    setCheckSequenceEnabled(organization.checkSequenceEnabled || false);
    setStartingCheckNumberError('');
    setLogo(null);
    setDisplayWidth(null);
    setDisplayHeight(null);
    setLogoPreview((current) => {
      if (current?.startsWith('blob:')) {
        URL.revokeObjectURL(current);
      }
      return organization.logo
        ? PUBLIC_ASSETS_BASE_URL + organization.logo
        : null;
    });
  }, [organization]);

  const saveMembership = async (managed, confirmUnassignment = false) => {
    try {
      const response = await updateMembership(
        organization.id,
        managed,
        confirmUnassignment,
      );
      onManagedAccountMembershipUpdated(response.data.data.managed_account_id);
      toast.success(
        t(
          managed
            ? 'organizations.update.membership.assigned'
            : 'organizations.update.membership.unassigned',
        ),
      );
    } catch {
      setIsManaged(organization.managedAccountId != null);
    }
  };

  const handleMembershipChange = (event) => {
    if (event.target.checked) {
      requestConfirmation(
        t('organizations.update.membership.assignWarning'),
        () => {
          setIsManaged(true);
          void saveMembership(true);
        },
        () => setIsManaged(false),
      );
      return;
    }

    requestConfirmation(
      t('organizations.update.membership.unassignWarning'),
      () => {
        setIsManaged(false);
        void saveMembership(false, true);
      },
      () => setIsManaged(true),
    );
  };

  useEffect(() => {
    return () => {
      if (logoPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);
  const [name, setName] = useState(organization.name || '');
  const [automatedAchReturnEnabled, setAutomatedAchReturnEnabled] = useState(
    organization.automatedAchReturnEnabled || false,
  );
  const [features, setFeatures] = useState({
    showApprovalsPage: false,
    showCreatePaymentPage: false,
    showBusinessUnitsPage: false,
    showFacilityManagementPage: false,
    showBulkApproveDecline: true,
    showDashboardTangoModal: false,
    enforceClientTransactionIdUniqueness: false,
    disableLegacyEmailTemplates: false,
    enableRegistrationPortal: false,
    enableClientAddressValidationBypass: false,
  });

  const [formValues, setFormValues] = useState({
    audience_designators: organization.audienceDesignators || [],
    payment_rails_designators: organization.paymentRailsDesignators || [],
  });

  useEffect(() => {
    setFormValues({
      audience_designators: organization.audienceDesignators || [],
      payment_rails_designators: organization.paymentRailsDesignators || [],
    });
  }, [organization]);

  const handleCity = (e) => setCity(e.target.value);
  const handleState = (e) => setState(e.target.value);
  const handleCounty = (e) => setCounty(e.target.value);
  const handleSicCode = (e) => setSicCode(e.target.value);
  const handleStateOfIncorporation = (e) =>
    setStateOfIncorporation(e.target.value);
  const handleIncorporationDate = (date) => setIncorporationDate(date);
  const handleTaxIdExt = (e) => setTaxIdExt(e.target.value);
  const handleTaxId = (e) => setTaxId(e.target.value);
  const handleNaicsCode = (e) => setNaicsCode(e.target.value);
  const handleName = (e) => setName(e.target.value);
  const handleSsoDirectLink = (e) => {
    setSsoDirectLink(e.target.value);
    setSsoDirectLinkError('');
  };
  const handleStartingCheckNumber = (e) => {
    setStartingCheckNumber(e.target.value.replace(/\D/g, '').slice(0, 10));
    setStartingCheckNumberError('');
  };
  const handleCheckSequenceEnabled = (e) => {
    setCheckSequenceEnabled(e.target.checked);
    setStartingCheckNumberError('');
  };

  const handleLogoChange = ({
    file,
    previewUrl,
    displayWidth: width,
    displayHeight: height,
  }) => {
    setLogo(file);
    setLogoPreview((current) => {
      if (current?.startsWith('blob:')) {
        URL.revokeObjectURL(current);
      }
      return previewUrl;
    });
    setDisplayWidth(width);
    setDisplayHeight(height);
  };

  const onAudienceDesignatorChange = (value, index) => {
    const updatedValues = formValues.audience_designators.map((v, i) =>
      i === index ? value : v,
    );
    setFormValues({
      ...formValues,
      audience_designators: updatedValues,
    });
  };

  const removeAudienceDesignator = (index) => {
    const updatedValues = formValues.audience_designators.filter(
      (_, i) => i !== index,
    );
    setFormValues({
      ...formValues,
      audience_designators: updatedValues,
    });
  };

  const addAudienceDesignator = () => {
    setFormValues((values) => ({
      ...values,
      audience_designators: [...(values.audience_designators || []), ''],
    }));
  };

  const onPaymentRailDesignatorChange = (value, index) => {
    const updatedValues = formValues.payment_rails_designators.map((v, i) =>
      i === index ? value : v,
    );
    setFormValues({
      ...formValues,
      payment_rails_designators: updatedValues,
    });
  };

  const removePaymentRailDesignator = (index) => {
    const updatedValues = formValues.payment_rails_designators.filter(
      (_, i) => i !== index,
    );
    setFormValues({
      ...formValues,
      payment_rails_designators: updatedValues,
    });
  };

  const addPaymentRailDesignator = () => {
    setFormValues((values) => ({
      ...values,
      payment_rails_designators: [
        ...(values.payment_rails_designators || []),
        '',
      ],
    }));
  };

  const trimmedStartingCheckNumber = startingCheckNumber.trim();
  const isStartingCheckNumberFormatValid = /^\d{4,10}$/.test(
    trimmedStartingCheckNumber,
  );
  const hasStartingCheckNumberFormatError =
    checkSequenceEnabled &&
    trimmedStartingCheckNumber !== '' &&
    !isStartingCheckNumberFormatValid;

  const {
    verdict: checkSequenceVerdict,
    loading: checkSequenceValidating,
    failed: checkSequenceValidationFailed,
  } = useValidateCheckSequence({
    organizationId: organization.id,
    checkSequenceEnabled,
    startingCheckNumber:
      checkSequenceEnabled && isStartingCheckNumberFormatValid
        ? Number(trimmedStartingCheckNumber)
        : null,
    skip: hasStartingCheckNumberFormatError,
  });

  const handleUpdate = async () => {
    const trimmedSsoDirectLink = ssoDirectLink.trim();
    if (
      trimmedSsoDirectLink &&
      (!isValidHttpUrl(trimmedSsoDirectLink) ||
        !trimmedSsoDirectLink.toLowerCase().startsWith('https://'))
    ) {
      setSsoDirectLinkError(t('organizations.update.ssoDirectLinkInvalid'));
      return;
    }

    if (checkSequenceEnabled && !isStartingCheckNumberFormatValid) {
      setStartingCheckNumberError(
        t('organizations.update.startingCheckNumberInvalid'),
      );
      return;
    }

    const formData = new FormData();
    formData.append('name', name);
    formData.append('city', city);
    formData.append('state', state);
    formData.append('county', county);
    formData.append('sicCode', sicCode);
    formData.append('stateOfIncorporation', stateOfIncorporation);
    formData.append(
      'incorporationDate',
      incorporationDate ? moment(incorporationDate).format('YYYY-MM-DD') : '',
    );
    formData.append('taxIdExt', taxIdExt);
    formData.append('taxId', taxId);
    formData.append('naicsCode', naicsCode);
    formData.append('helpEn', helpEn);
    formData.append('helpFr', helpFr);
    formData.append('helpEs', helpEs);
    formData.append('ssoDirectLink', trimmedSsoDirectLink);
    formData.append('checkSequenceEnabled', checkSequenceEnabled);
    if (isStartingCheckNumberFormatValid) {
      formData.append('startingCheckNumber', trimmedStartingCheckNumber);
    }
    formData.append('automatedAchReturnEnabled', automatedAchReturnEnabled);
    formData.append('features', JSON.stringify(features));
    formData.append(
      'audienceDesignators',
      JSON.stringify(formValues.audience_designators),
    );
    formData.append(
      'paymentRailsDesignators',
      JSON.stringify(formValues.payment_rails_designators),
    );

    if (logo instanceof File) {
      appendLogoToFormData(formData, 'logo', {
        file: logo,
        displayWidth,
        displayHeight,
      });
    }

    await updateOrganization(formData);
  };

  return (
    <>
      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            type='text'
            labelText={t('organizations.update.nameLabel')}
            onChange={handleName}
            value={name}
            id='name'
            name='name'
            formControlProps={{
              fullWidth: true,
            }}
          />
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <LogoUpload
            inputId='organization-logo'
            label={t('organizations.update.uploadLogoLabel')}
            browseLabel={t('organizations.update.browseLogoButton')}
            previewUrl={logo instanceof File ? logoPreview : null}
            existingPreviewUrl={!(logo instanceof File) ? logoPreview : null}
            onChange={handleLogoChange}
          />
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText={t('organizations.update.cityLabel')}
            id='city'
            name='city'
            onChange={handleCity}
            value={city}
            type='text'
            formControlProps={{
              fullWidth: true,
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText={t('organizations.update.stateLabel')}
            id='state'
            name='state'
            onChange={handleState}
            value={state}
            type='text'
            formControlProps={{
              fullWidth: true,
            }}
          />
        </GridItem>

        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText={t('organizations.update.countyLabel')}
            id='county'
            name='county'
            onChange={handleCounty}
            value={county}
            type='text'
            formControlProps={{
              fullWidth: true,
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText={t('organizations.update.sicCodeLabel')}
            id='sicCode'
            name='sicCode'
            onChange={handleSicCode}
            value={sicCode}
            type='text'
            maxLength={4}
            formControlProps={{
              fullWidth: true,
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText={t('organizations.update.stateOfIncorporationLabel')}
            id='stateOfIncorporation'
            name='stateOfIncorporation'
            onChange={handleStateOfIncorporation}
            value={stateOfIncorporation}
            type='text'
            maxLength={2}
            formControlProps={{
              fullWidth: true,
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <div className='custom-select'>
            <FormControl fullWidth className={classes.formControl}>
              <DatePicker
                selected={incorporationDate}
                onChange={handleIncorporationDate}
                dateFormat='MM/dd/yyyy'
                placeholderText={t(
                  'organizations.update.incorporationDatePlaceholder',
                )}
                isClearable
                customInput={
                  <TextField
                    variant='standard'
                    label={t('organizations.update.incorporationDateLabel')}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      classes: {
                        underline: classes.underline,
                      },
                    }}
                    sx={{
                      '& .MuiInput-input': {
                        padding: '4px 0 5px 0',
                        height: '23px',
                        lineHeight: '23px',
                      },
                    }}
                  />
                }
              />
            </FormControl>
          </div>
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText={t('organizations.update.taxIdExtLabel')}
            id='taxIdExt'
            name='taxIdExt'
            onChange={handleTaxIdExt}
            value={taxIdExt}
            type='text'
            maxLength={4}
            formControlProps={{
              fullWidth: true,
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText={t('organizations.update.taxIdLabel')}
            id='taxId'
            name='taxId'
            onChange={handleTaxId}
            value={taxId}
            type='text'
            maxLength={9}
            formControlProps={{
              fullWidth: true,
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText={t('organizations.update.naicsCodeLabel')}
            id='naicsCode'
            name='naicsCode'
            onChange={handleNaicsCode}
            value={naicsCode}
            type='text'
            maxLength={6}
            formControlProps={{
              fullWidth: true,
            }}
          />
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <CustomInput
            labelText={t('organizations.update.ssoDirectLinkLabel')}
            placeholder='https://sso.example.com/org'
            id='ssoDirectLink'
            name='ssoDirectLink'
            onChange={handleSsoDirectLink}
            value={ssoDirectLink}
            type='text'
            error={!!ssoDirectLinkError}
            formControlProps={{
              fullWidth: true,
            }}
            inputProps={{
              helperText:
                ssoDirectLinkError ||
                t('organizations.update.ssoDirectLinkHelperText'),
            }}
          />
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
              <CustomInput
                labelText={t('organizations.update.startingCheckNumberLabel')}
                id='startingCheckNumber'
                name='startingCheckNumber'
                onChange={handleStartingCheckNumber}
                value={startingCheckNumber}
                type='text'
                disabled={!checkSequenceEnabled}
                error={!!startingCheckNumberError}
                formControlProps={{
                  fullWidth: true,
                }}
                inputProps={{
                  helperText:
                    startingCheckNumberError ||
                    t('organizations.update.startingCheckNumberHelperText'),
                  inputProps: { inputMode: 'numeric', maxLength: 10 },
                }}
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={checkSequenceEnabled}
                  onChange={handleCheckSequenceEnabled}
                  name='check_sequence_enabled'
                  color='primary'
                />
              }
              label={t('organizations.update.checkSequence.toggle')}
              sx={{
                mr: 0,
                flexShrink: 0,
                maxWidth: '45%',
                '& .MuiFormControlLabel-label': { fontSize: '0.9rem' },
              }}
            />
          </Box>
          <CheckSequenceValidationBanner
            verdict={checkSequenceVerdict}
            loading={checkSequenceValidating}
            failed={checkSequenceValidationFailed}
            localErrorMessage={
              hasStartingCheckNumberFormatError
                ? t('organizations.update.startingCheckNumberInvalid')
                : ''
            }
            showMinimum={checkSequenceEnabled}
          />
        </GridItem>
      </GridContainer>
      {canManageManagedAccounts && (
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={isManaged}
                  onChange={handleMembershipChange}
                  disabled={membershipLoading}
                  color='primary'
                />
              }
              label={t('organizations.update.membership.toggle')}
              sx={{ my: 2 }}
            />
          </GridItem>
        </GridContainer>
      )}
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Typography
            variant='subtitle2'
            sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
            {t('organizations.update.helpContentLabel')}
          </Typography>
        </GridItem>
        {[
          {
            label: t('organizations.update.helpEnLabel'),
            value: helpEn,
            setter: setHelpEn,
          },
          {
            label: t('organizations.update.helpFrLabel'),
            value: helpFr,
            setter: setHelpFr,
          },
          {
            label: t('organizations.update.helpEsLabel'),
            value: helpEs,
            setter: setHelpEs,
          },
        ].map(({ label, value, setter }) => (
          <GridItem key={label} xs={12} sm={12} md={12}>
            <TextField
              label={label}
              value={value}
              onChange={(e) => setter(e.target.value)}
              multiline
              minRows={1}
              maxRows={4}
              fullWidth
              variant='standard'
              sx={{ mb: 1 }}
            />
          </GridItem>
        ))}
      </GridContainer>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <FormControlLabel
            control={
              <Switch
                checked={automatedAchReturnEnabled}
                onChange={(e) => setAutomatedAchReturnEnabled(e.target.checked)}
                name='automated_ach_return_enabled'
                color='primary'
              />
            }
            label={t('organizations.update.achReturnLabel')}
            sx={{
              my: 2,
              '& .MuiFormControlLabel-label': { fontSize: '0.9rem' },
            }}
          />
        </GridItem>
      </GridContainer>
      <OrganizationFeatures
        orgId={organization.id}
        features={features}
        setFeatures={setFeatures}
      />

      <AudienceDesignator
        formValues={formValues}
        isUpdating={() => true}
        onAudienceDesignatorChange={onAudienceDesignatorChange}
        removeAudienceDesignator={removeAudienceDesignator}
        addAudienceDesignator={addAudienceDesignator}
      />

      <PaymentRailDesignator
        formValues={formValues}
        isUpdating={() => true}
        onPaymentRailDesignatorChange={onPaymentRailDesignatorChange}
        removePaymentRailDesignator={removePaymentRailDesignator}
        addPaymentRailDesignator={addPaymentRailDesignator}
      />

      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <Button color='primary' onClick={handleUpdate}>
            {t('common.buttons.update')}
          </Button>
        </GridItem>
        <GridItem xs={12} sm={12} md={6}></GridItem>
      </GridContainer>
    </>
  );
};

export default GeneralTab;
